import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../utils/api';
import { OffertifyLogo } from '../components/OffertifyLogo';

// Policy allineata al backend (POST /auth/reset-password) e a MyProfile:
// min 8 + almeno 1 maiuscola + 1 minuscola + 1 cifra.
const resetPasswordSchema = z
  .object({
    new_password: z
      .string()
      .min(8, 'Minimo 8 caratteri')
      .regex(/[A-Z]/, 'Deve contenere almeno una lettera maiuscola')
      .regex(/[a-z]/, 'Deve contenere almeno una lettera minuscola')
      .regex(/[0-9]/, 'Deve contenere almeno un numero'),
    confirm_password: z.string().min(1, 'Conferma la password'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Le password non coincidono',
    path: ['confirm_password'],
  });

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

// Il backend restituisce errori come `detail`: stringa (es. 400 invalid_or_expired_token)
// oppure array pydantic (422 [{ msg }]). Estrae un messaggio leggibile in entrambi i casi.
const extractApiError = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return error?.response?.data?.message || fallback;
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { new_password: '', confirm_password: '' },
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    try {
      await authAPI.resetPassword({ token, new_password: data.new_password });
      toast.success('Password reimpostata con successo. Ora puoi accedere.');
      navigate('/login', { replace: true });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      // 400: il backend restituisce lo slug grezzo 'invalid_or_expired_token'
      if (error?.response?.status === 400 || detail === 'invalid_or_expired_token') {
        toast.error('Link scaduto o non valido. Richiedi un nuovo link di reset.');
        return;
      }
      // 422 (policy password) o altri errori: mostra il messaggio del backend
      toast.error(extractApiError(error, 'Impossibile reimpostare la password. Riprova.'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 flex items-center justify-center rounded-2xl bg-primary-50 shadow-sm border border-primary-100">
            <OffertifyLogo className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-secondary-900">
            Nuova password
          </h2>
          <p className="mt-2 text-sm text-secondary-600">
            Scegli una nuova password per il tuo account
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg border border-secondary-200 p-8">
          {!token ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-secondary-700">
                Link di reset non valido o incompleto.
              </p>
              <p className="text-sm text-secondary-600">
                Richiedi un nuovo link dalla pagina{' '}
                <Link to="/forgot-password" className="font-semibold text-primary-600 hover:text-primary-700">
                  Password dimenticata
                </Link>
                .
              </p>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-secondary-700 mb-1">
                  Nuova password
                </label>
                <div className="relative">
                  <input
                    id="new_password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="w-full px-4 py-2.5 pr-11 border border-secondary-300 rounded-lg text-secondary-900 bg-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all sm:text-sm"
                    placeholder="Min 8 caratteri, 1 maiuscola, 1 minuscola, 1 numero"
                    {...register('new_password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary-400 hover:text-secondary-600 focus:outline-none focus:text-primary-600 transition-colors"
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                    title={showPassword ? 'Nascondi password' : 'Mostra password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.new_password ? (
                  <p className="mt-1 text-sm text-red-600">{errors.new_password.message}</p>
                ) : (
                  <p className="mt-1 text-xs text-secondary-500">
                    Almeno 8 caratteri, con una maiuscola, una minuscola e un numero.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-secondary-700 mb-1">
                  Conferma password
                </label>
                <input
                  id="confirm_password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 bg-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all sm:text-sm"
                  placeholder="Ripeti la password"
                  {...register('confirm_password')}
                />
                {errors.confirm_password && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirm_password.message}</p>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Salvataggio...
                    </div>
                  ) : (
                    'Reimposta password'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-8 text-center text-sm text-secondary-600">
          <Link to="/login" className="inline-flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Torna al login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
